import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  CssBaseline,
  Button,
} from '@mui/material';
import NavigationBar from '../components/layout/NavigationBar';
import Footer from '../components/layout/Footer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const colors = {
  black: '#000000',
  darkRed: '#451a15',
  red: '#970000',
  yellow: '#fdb200',
  offWhite: '#f6ede7',
};

function News() {
  const { t, i18n } = useTranslation();

  // Sample news data - in production this would come from an API/CMS
  const newsArticles = [
    {
      id: 1,
      date: '2024-01-15',
      category: t('news.categories.product'),
      title: t('news.articles.1.title'),
      excerpt: t('news.articles.1.excerpt'),
      readTime: '3 min',
    },
    {
      id: 2,
      date: '2024-01-10',
      category: t('news.categories.legal'),
      title: t('news.articles.2.title'),
      excerpt: t('news.articles.2.excerpt'),
      readTime: '5 min',
    },
    {
      id: 3,
      date: '2024-01-05',
      category: t('news.categories.company'),
      title: t('news.articles.3.title'),
      excerpt: t('news.articles.3.excerpt'),
      readTime: '4 min',
    },
  ];

  const getCategoryColor = (category) => {
    const categoryKey = Object.entries(t('news.categories', { returnObjects: true }))
      .find(([key, value]) => value === category)?.[0];

    switch(categoryKey) {
      case 'product': return colors.yellow;
      case 'legal': return colors.red;
      case 'company': return colors.darkRed;
      default: return colors.darkRed;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString(i18n.language === 'hr' ? 'hr-HR' : 'en-US', options);
  };

  return (
    <>
      <CssBaseline />
      <Box sx={{ backgroundColor: colors.offWhite, minHeight: '100vh' }}>
        <NavigationBar />

        {/* Spacer for fixed navbar */}
        <Box sx={{ height: '64px' }} />

        {/* Hero Section */}
        <Box
          sx={{
            py: 8,
            backgroundColor: colors.offWhite,
            textAlign: 'center',
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                color: colors.darkRed,
                mb: 2,
                fontWeight: 'bold',
              }}
            >
              {t('news.title')}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: colors.darkRed,
                mb: 6,
                opacity: 0.9,
              }}
            >
              {t('news.subtitle')}
            </Typography>
          </Container>
        </Box>

        {/* News Articles Grid */}
        <Container maxWidth="lg" sx={{ pb: 8 }}>
          <Grid container spacing={4}>
            {newsArticles.map((article) => (
              <Grid item xs={12} md={4} key={article.id}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    height: '100%',
                    backgroundColor: 'white',
                    borderTop: `4px solid ${getCategoryColor(article.category)}`,
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  {/* Date and Category */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon sx={{ fontSize: 16, color: colors.darkRed, opacity: 0.7 }} />
                      <Typography variant="caption" sx={{ color: colors.darkRed, opacity: 0.7 }}>
                        {formatDate(article.date)}
                      </Typography>
                    </Box>
                    <Chip
                      label={article.category}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(article.category),
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    sx={{
                      color: colors.darkRed,
                      fontWeight: 'bold',
                      mb: 2,
                      lineHeight: 1.3,
                    }}
                  >
                    {article.title}
                  </Typography>

                  {/* Excerpt */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: colors.darkRed,
                      mb: 3,
                      flexGrow: 1,
                      opacity: 0.9,
                      lineHeight: 1.6,
                    }}
                  >
                    {article.excerpt}
                  </Typography>

                  {/* Read More */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ color: colors.darkRed, opacity: 0.7 }}>
                      {article.readTime} {t('news.read')}
                    </Typography>
                    <Button
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        color: colors.red,
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: 'rgba(151, 0, 0, 0.08)',
                        },
                      }}
                    >
                      {t('news.read_more')}
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Load More Section */}
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: colors.red,
                color: colors.yellow,
                fontWeight: 'bold',
                borderRadius: '8px',
                px: 4,
                py: 1.5,
                transition: 'all 0.3s ease',
                '&:hover': { backgroundColor: colors.darkRed },
              }}
            >
              {t('news.load_more')}
            </Button>
          </Box>
        </Container>

        <Footer />
      </Box>
    </>
  );
}

export default News;
